import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Store,
  CalendarDays,
  BarChart3,
  Briefcase,
  ShoppingBag,
  HeartHandshake,
  Megaphone,
  Handshake,
  Gift,
  Percent,
  Images,
  LogOut,
  Moon,
  Sun,
  Monitor,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { Separator } from 'src/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'src/components/ui/tooltip';
import { useAuth } from 'src/contexts/AuthContext';
import { useTheme } from 'src/contexts/ThemeContext';
import Logo from './Logo';

export const NAV_ITEMS = [
  { href: '/app/dashboard', icon: LayoutDashboard, title: 'Tableau de bord' },
  { href: '/app/salons', icon: Store, title: 'Salons' },
  { href: '/app/appointements', icon: CalendarDays, title: 'Rendez-vous' },
  { href: '/app/kpi', icon: BarChart3, title: 'KPIs' },
  { href: '/app/specialties', icon: Briefcase, title: 'Spécialités' },
  { href: '/app/services', icon: ShoppingBag, title: 'Services' },
  { href: '/app/prestation', icon: HeartHandshake, title: 'Prestations' },
  { href: '/app/our-pix', icon: Images, title: 'Our pix' },
  { href: '/app/ads', icon: Megaphone, title: 'Publicités' },
  { href: '/app/wishlist-partner', icon: Handshake, title: 'Wishlist partner' },
  { href: '/app/gift-card', icon: Gift, title: 'Cartes cadeaux' },
  { href: '/app/commission', icon: Percent, title: 'Commissions' }
];

function NavList({ collapsed, onNavigate }) {
  const location = useLocation();

  return (
    <nav className="space-y-1 px-2">
      {NAV_ITEMS.map((item) => {
        const active = location.pathname.startsWith(item.href);
        const link = (
          <NavLink
            to={item.href}
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              active
                ? 'bg-sidebar-accent text-primary'
                : 'text-sidebar-foreground/80 hover:bg-muted hover:text-foreground',
              collapsed && 'justify-center px-2'
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed ? <span className="truncate">{item.title}</span> : null}
          </NavLink>
        );

        if (!collapsed) return <div key={item.href}>{link}</div>;

        return (
          <Tooltip key={item.href}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{item.title}</TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );
}

export default function AdminSidebar({
  collapsed,
  onToggle,
  onNavigate,
  className
}) {
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();

  const themeOptions = [
    { value: 'light', icon: Sun, label: 'Clair' },
    { value: 'dark', icon: Moon, label: 'Sombre' },
    { value: 'system', icon: Monitor, label: 'Système' }
  ];

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
        collapsed ? 'w-[72px]' : 'w-64',
        className
      )}
    >
      <div className={cn('flex h-16 items-center gap-2.5 px-3', collapsed && 'justify-center px-2')}>
        <Logo size={collapsed ? 'sm' : 'md'} />
        {!collapsed ? (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">IZYA Admin</p>
            <p className="truncate text-xs text-muted-foreground">Administration</p>
          </div>
        ) : null}
      </div>
      <Separator />
      <ScrollArea className="flex-1 py-3">
        <NavList collapsed={collapsed} onNavigate={onNavigate} />
      </ScrollArea>
      <Separator />
      <div className={cn('space-y-2 p-3', collapsed && 'px-2')}>
        <div className={cn('flex gap-1', collapsed && 'flex-col')}>
          {themeOptions.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={theme === option.value ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => setTheme(option.value)}
              aria-label={option.label}
            >
              <option.icon className="h-4 w-4" />
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {onToggle ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggle}
              aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className={cn('h-8 flex-1 justify-start gap-2', collapsed && 'flex-none px-2')}
            onClick={signOut}
          >
            <LogOut className="h-4 w-4" />
            {!collapsed ? 'Déconnexion' : null}
          </Button>
        </div>
      </div>
    </aside>
  );
}
